import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import LandingPage from "./pages/LandingPage";
import ServicesTarifsPage from "./pages/ServicesTarifsPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MagicLoginPage from "./pages/MagicLoginPage";
import FirstLoginFlow from "./pages/FirstLoginFlow";
import ClientDashboard from "./pages/ClientDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import { CGVPage, MentionsLegalesPage, PolitiqueCookiesPage, PolitiqueConfidentialitePage } from "./pages/LegalPages";
import { BlogListPage, BlogArticlePage } from "./pages/BlogPage";
import FAQPage from "./pages/FAQPage";
import DemoRequestPage from "./pages/DemoRequestPage";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "./components/CookieConsent";
import { Loader2 } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#3F6B6B]" />
    </div>
  );
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;
  }
  if (
    user.role === "client" &&
    (user.first_login || !user.password_changed || !user.contract_signed) &&
    !window.location.pathname.startsWith("/first-login")
  ) {
    return <Navigate to="/first-login" replace />;
  }
  return children;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.first_login || !user.password_changed || !user.contract_signed) {
      return <Navigate to="/first-login" replace />;
    }
    return <Navigate to="/client" replace />;
  }
  return children;
}

function GAPageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function" && !window["ga-disable-G-SGKLLG8073"]) {
      window.gtag("event", "page_view", { page_path: location.pathname + location.search });
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
    <div className="App">
      <BrowserRouter>
        <GAPageTracker />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services-et-tarifs" element={<ServicesTarifsPage />} />
            <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/magic-login" element={<MagicLoginPage />} />
            <Route
              path="/first-login"
              element={<ProtectedRoute><FirstLoginFlow /></ProtectedRoute>}
            />
            <Route
              path="/client/*"
              element={<ProtectedRoute requiredRole="client"><ClientDashboard /></ProtectedRoute>}
            />
            <Route
              path="/admin/*"
              element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>}
            />
            <Route path="/cgv" element={<CGVPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-cookies" element={<PolitiqueCookiesPage />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/demande-de-demo" element={<DemoRequestPage />} />
            <Route path="/contact" element={<DemoRequestPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsent />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
